// Server-sent events over fetch, in both directions of the app.
//
// EventSource is not used anywhere: it cannot send an Authorization header, and both hops
// carry one (the browser's Entra token, the server's runner key). So the stream is a plain
// `text/event-stream` body read with fetch, and this is the whole protocol — one JSON object
// per `data:` line, events separated by a blank line.

export function encodeSse(event: unknown): string {
  return `data: ${JSON.stringify(event)}\n\n`;
}

/** A comment line. Keeps proxies from closing a stream that is quiet while the agent thinks. */
export const SSE_HEARTBEAT = ": keep-alive\n\n";

export async function* parseSse<T>(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<T, void, undefined> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const chunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const event = parseChunk<T>(chunk);
        if (event !== undefined) yield event;
        boundary = buffer.indexOf("\n\n");
      }
    }
    const tail = parseChunk<T>(buffer);
    if (tail !== undefined) yield tail;
  } finally {
    reader.releaseLock();
  }
}

function parseChunk<T>(chunk: string): T | undefined {
  const data = chunk
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart())
    .join("\n");
  if (!data) return undefined;
  return JSON.parse(data) as T;
}
